package com.downdogapp.client.controllers.playback

import com.downdogapp.*
import com.downdogapp.client.*
import com.downdogapp.client.api.*
import com.downdogapp.client.api.RegenType.REWIND
import com.downdogapp.client.api.SongFeedback.THUMBS_DOWN
import com.downdogapp.client.singleton.App
import com.downdogapp.client.singleton.Network

class SongsController(initialPlaylist: Playlist?,
                      private val sequenceId: String,
                      private val playbackViewController: PlaybackViewController) : LogEmitter {

  private val currentSequenceTime get() = playbackViewController.currentSequenceTime
  private var fetchingNewPlaylist = false
  private val songsPlayed = mutableListOf<Song>()
  private var onSongUpdated: (() -> Unit) = {}

  var playlist: Playlist? = initialPlaylist; private set
  private var currentSong: Song? = null
    private set(value) {
      field = value
      if (value != null) {
        recordSongPlayIfNeeded()
      }
      onSongUpdated()
    }

  val songAttribution: String?
    get() =
      currentSong?.let { song ->
        if (playlist == null || fetchingNewPlaylist)
          null
        else
          "${song.title} $BULLET_UNICODE︎ ${song.artist}"
      }

  init {
    playlist?.let {
      Network.post(
        RecordPlaylistStartedRequest(
          playlistId = it.playlistId,
          offline = playbackViewController.isOffline
        )
      )
    }
  }

  fun setOnSongUpdated(callback: (() -> Unit)?) {
    onSongUpdated = callback ?: {}
  }

  fun updateCurrentSongIfNeeded() {
    playlist?.let { playlist ->
      val songIndex = binarySearch(playlist.startTimes, currentSequenceTime)
      val song = playlist.songs[songIndex]
      if (song != currentSong) {
        currentSong = song
      }
    }
  }

  fun skipSong(callback: (success: Boolean) -> Unit) {
    if (!fetchingNewPlaylist) {
      playlist?.let { playlist ->
        currentSong?.let { song ->
          Network.post(
            SetSongFeedbackRequest(
              songId = song.id,
              playlistId = playlist.playlistId,
              feedback = THUMBS_DOWN
            )
          )
        }
        fetchNewPlaylists(
          regenType = RegenType.SKIP,
          rewindAmount = null,
          callback = callback
        )
      } ?: callback(false)
    } else {
      callback(false)
    }
  }

  fun fetchNewPlaylistFromRewind(rewindAmount: Duration, callback: (success: Boolean) -> Unit) =
    playlist?.let {
      fetchNewPlaylists(REWIND, rewindAmount, callback)
    } ?: callback(false)

  fun onPlaylistSettingChanged(callback: ((Boolean) -> Unit)) {
    if (fetchingNewPlaylist) {
      App.alert(message = Strings.PLAYLIST_TYPE_BUSY) {
        App.updateFullscreen()
        callback(false)
      }
    } else {
      fetchNewPlaylists(
        regenType = RegenType.CHANGE_TYPE,
        rewindAmount = null,
        callback = callback
      )
    }
  }

  // Callback will be called before playback urls are fetched
  private fun fetchNewPlaylists(regenType: RegenType,
                                rewindAmount: Duration?,
                                callback: (Boolean) -> Unit = {}) {
    fetchingNewPlaylist = true
    val request = RegenPlaylistRequest(
      sequenceId = sequenceId,
      sequenceTime = currentSequenceTime,
      regenType = regenType,
      rewindAmount = rewindAmount,
      playlistSettings = SequenceSettings.playlistSettings
    )
    Network.post(request) { json ->
      fetchingNewPlaylist = false
      if (json != null) {
        val response = RegenPlaylistResponse.fromJson(json)
        if (response.errorMessage != null) {
          App.alert(message = response.errorMessage) {
            App.updateFullscreen()
            updateCurrentSongIfNeeded()
          }
          callback(false)
        } else {
          logEvent(
            "fetch_new_playlists",
            mapOf(
              "regenType" to regenType.toString(),
              "oldPlaylistId" to playlist?.playlistId,
              "newPlaylistId" to response.playlist?.playlistId,
              "rewindAmount" to rewindAmount?.seconds?.toString()
            )
          )
          playlist = response.playlist
          updateCurrentSongIfNeeded()
          playlist?.let {
            Network.post(
              RecordPlaylistStartedRequest(
                playlistId = it.playlistId,
                regenType = regenType,
                offline = playbackViewController.isOffline
              )
            )
          }
          callback(true)
        }
      } else {
        App.alertNoInternet {
          App.updateFullscreen()
          updateCurrentSongIfNeeded()
        }
        callback(false)
      }
    }
  }

  fun recordSongPlayIfNeeded() {
    if (playbackViewController.isPlaying) {
      playlist?.let { playlist ->
        currentSong?.let { song ->
          if (songsPlayed.isEmpty() || songsPlayed.last().id != song.id) {
            songsPlayed.add(song)
            Network.post(
              RecordSongPlayRequest(
                songId = song.id,
                playlistId = playlist.playlistId
              )
            )
          }
        }
      }
    }
  }

  fun getSongsPlayed(): List<Song> = songsPlayed
}
